/**
 * Core Select / Dropdown Component
 *
 * Enterprise-grade select supporting single/multi selection, async search,
 * grouping, creatable items, clearable, and RBAC gating.
 * Built on MUI Autocomplete for keyboard nav, virtualization, and theming.
 *
 * @example
 *   <Select label="Status" options={statusOptions} value={val} onChange={setVal} />
 *   <Select label="Tags" options={tagOpts} value={tags} onChange={setTags} multiple searchable creatable />
 */
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Autocomplete, TextField, Chip, CircularProgress,
  InputAdornment, createFilterOptions,
} from '@mui/material';
import { ChevronDown, X, Search } from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

// ── Option shape ────────────────────────────────────────────────────────
export interface SelectOption {
  value: string;
  label: string;
  /** Optional group heading (for grouped selects) */
  group?: string;
  /** Icon rendered before label */
  icon?: React.ReactNode;
  /** Disable this option */
  disabled?: boolean;
  /** Any extra payload for callbacks */
  meta?: Record<string, unknown>;
}

// ── Props ───────────────────────────────────────────────────────────────
export interface SelectProps {
  /** Static options list */
  options: SelectOption[];

  /* ── Value (controlled) ────────────────────────────────────────── */
  /** Current value: string for single-select, string[] for multi-select */
  value?: string | string[] | null;
  /** Change callback — receives value string(s), not the full option */
  onChange?: (value: string | string[] | null, option?: SelectOption | SelectOption[] | null) => void;

  /* ── Behaviour ─────────────────────────────────────────────────── */
  /** Enable multi-select mode */
  multiple?: boolean;
  /** Enable typing to filter options */
  searchable?: boolean;
  /** Allow user to create new options on the fly */
  creatable?: boolean;
  /** Show clear button */
  clearable?: boolean;
  /** Group options by `option.group` */
  grouped?: boolean;

  /* ── Async loading ─────────────────────────────────────────────── */
  /** Show spinner in dropdown (for async fetch) */
  loading?: boolean;
  /** Callback when search input changes (for server-side search) */
  onSearch?: (query: string) => void;

  /* ── Visual / form ─────────────────────────────────────────────── */
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Max tags shown in multi-select before "+N" chip */
  limitTags?: number;
  /** Icon before the input */
  startIcon?: React.ReactNode;
  /** Custom "no options" message */
  noOptionsText?: string;

  /* ── RBAC ──────────────────────────────────────────────────────── */
  /** Hide entire select if user lacks this permission */
  permission?: string;
}

// ── Creatable filter ────────────────────────────────────────────────────
const creatableFilter = createFilterOptions<SelectOption>();

// ── Component ───────────────────────────────────────────────────────────
export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      multiple = false,
      searchable = true,
      creatable = false,
      clearable = true,
      grouped = false,
      loading = false,
      onSearch,
      label,
      placeholder,
      helperText,
      error = false,
      required = false,
      disabled = false,
      readOnly = false,
      size = 'small',
      fullWidth = true,
      className = '',
      id,
      name,
      limitTags = 3,
      startIcon,
      noOptionsText = 'No options',
      permission,
    },
    ref,
  ) => {
    const { user } = useAuth();
    if (permission && !hasPermission(user, permission)) return null;

    /* ── Derive the selected option object(s) from value strings ──── */
    const selectedOption = useMemo(() => {
      if (value == null) return multiple ? [] : null;
      if (multiple) {
        const vals = Array.isArray(value) ? value : [value];
        return vals.map(v => options.find(o => o.value === v) || { value: v, label: v });
      }
      return options.find(o => o.value === (value as string)) || null;
    }, [value, options, multiple]);

    /* ── Handlers ────────────────────────────────────────────────── */
    const handleChange = useCallback(
      (_: unknown, newVal: SelectOption | SelectOption[] | null) => {
        if (!onChange) return;
        if (newVal == null) {
          onChange(multiple ? [] : null, newVal);
        } else if (Array.isArray(newVal)) {
          onChange(newVal.map(o => o.value), newVal);
        } else {
          onChange(newVal.value, newVal);
        }
      },
      [onChange, multiple],
    );

    return (
      <Autocomplete
        ref={ref}
        id={id}
        className={className}
        multiple={multiple}
        options={options}
        value={selectedOption as any}
        onChange={handleChange as any}
        loading={loading}
        disabled={disabled || readOnly}
        disableClearable={!clearable}
        size={size}
        fullWidth={fullWidth}
        limitTags={limitTags}
        noOptionsText={noOptionsText}
        getOptionLabel={(opt: SelectOption) => opt?.label ?? ''}
        getOptionDisabled={(opt: SelectOption) => !!opt.disabled}
        isOptionEqualToValue={(a: SelectOption, b: SelectOption) => a.value === b.value}
        groupBy={grouped ? (opt: SelectOption) => opt.group || '' : undefined}
        filterOptions={
          creatable
            ? (opts, params) => {
                const filtered = creatableFilter(opts, params);
                const inputVal = params.inputValue.trim();
                if (inputVal && !opts.some(o => o.value === inputVal)) {
                  filtered.push({ value: inputVal, label: `Create "${inputVal}"` });
                }
                return filtered;
              }
            : undefined
        }
        onInputChange={onSearch ? (_, val, reason) => { if (reason === 'input') onSearch(val); } : undefined}
        disableCloseOnSelect={multiple}
        renderTags={multiple ? (tagValues: SelectOption[], getTagProps) =>
          tagValues.map((opt, idx) => (
            <Chip
              {...getTagProps({ index: idx })}
              key={opt.value}
              label={opt.label}
              size="small"
              icon={opt.icon as React.ReactElement | undefined}
              sx={{ borderRadius: '6px' }}
            />
          )) : undefined
        }
        renderOption={(props, opt: SelectOption) => (
          <li {...props} key={opt.value}>
            {opt.icon && <span className="mr-2 flex items-center">{opt.icon}</span>}
            {opt.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            name={name}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            required={required}
            InputProps={{
              ...params.InputProps,
              readOnly,
              ...(startIcon
                ? { startAdornment: (
                    <>
                      <InputAdornment position="start">{startIcon}</InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )}
                : {}),
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-popupIndicator': { transform: 'none' },
        }}
      />
    );
  },
);
Select.displayName = 'Select';
export default Select;
